import React, { Fragment, useCallback } from 'react';
import {
  Icon,
  Popover,
  Text,
  Grid,
  GridRow,
  GridCol,
  ButtonV2,
  Heading,
  Image,
  Rule,
  Tooltip,
} from '@arc-ui/components';
import { ICON_BASKET, ICON_CLOSE, ICON_NOTIFICATION } from '../../atoms/SVG';
import { BasketPopoverComponentProps } from '../../../types/ee-header';
import { useGuestCart } from '../../../hooks/useGuestCart';
import { FALLBACK_IMG, ROW_HEIGHT, VISIBLE_ROWS } from '../../../constants';

const BasketPopover: React.FC<BasketPopoverComponentProps> = ({
  basketOverlayOpen,
  setBasketOverlayOpen,
  basketTriggerRef,
  linkToBasketPage,
  abandonedBasketButtonText,
  abandonedBasketTitle,
  isSmallScreen,
  sheetTop,
  renderTrigger,
}) => {
  const { items, loading, error, cartId } = useGuestCart({ enabled: true });
  const visibleItems = (items ?? []).slice(0, VISIBLE_ROWS);
  const listHeight =
    visibleItems.length > 0 ? visibleItems.length * ROW_HEIGHT : undefined;

  const toggleBasket = useCallback(() => {
    setBasketOverlayOpen(!basketOverlayOpen);
  }, [basketOverlayOpen, setBasketOverlayOpen]);

  const handleTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleBasket();
    }
  };

  const Content = (
    <div
      id="popover-content"
      style={{ maxHeight: '69vh', position: 'relative' }}
    >
      <button
        id="close-basket"
        onClick={() => setBasketOverlayOpen(false)}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
        aria-label="Close"
      >
        <Icon icon={ICON_CLOSE} size={24} label="Close" />
      </button>

      <Grid>
        <GridRow justify="normal">
          <GridCol xs={12} align={{ xs: 'center' }}>
            <Heading level="6" size="xxs" align="center" id="basket-title">
              {abandonedBasketTitle}
            </Heading>
          </GridCol>
        </GridRow>

        <GridRow>
          <GridCol xs={12}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 5,
              }}
              id="popover-notify"
            >
              <Icon icon={ICON_NOTIFICATION} size={14} label="Basket" />
              <Text size="xs" tone="supporting">
                Pick up where you left off:
              </Text>
            </div>
          </GridCol>
        </GridRow>

        {error && cartId && (
          <GridRow>
            <GridCol xs={12}>
              <Text elementType="p" size="s" tone="muted">
                Unable to load basket items
              </Text>
            </GridCol>
          </GridRow>
        )}

        {loading && (
          <GridRow>
            <GridCol xs={12}>
              <Text elementType="p" size="s" tone="muted">
                Loading basket...
              </Text>
            </GridCol>
          </GridRow>
        )}

        {!loading && !error && visibleItems.length > 0 && (
          <div
            style={{
              height: listHeight,
              overflowY: 'auto',
              paddingRight: 4,
            }}
            aria-label="Basket items"
          >
            <Grid isGutterless isFluid>
              {visibleItems.map((item, idx) => (
                <Fragment key={item.id ?? idx}>
                  <GridRow direction="row" align="start" justify="between">
                    <GridCol xs={2} s={2} m={2} l={3} xl={3}>
                      <Tooltip side="bottom" title={item.title}>
                        <div id="basket-item-image">
                          <Image
                            src={item.imageUrl ?? FALLBACK_IMG}
                            alt={item.title ?? 'image'}
                            width={50}
                            height={50}
                            fit="cover"
                            fadeOnLoad
                          />
                        </div>
                      </Tooltip>
                    </GridCol>

                    <GridCol xs={10} s={10} m={10} l={9} xl={9}>
                      <div id="basket-item-details">
                        <Text elementType="p" size="s">
                          {item.title}
                        </Text>
                        {item.inStock && (
                          <Text elementType="span" size="xs" tone="muted">
                            In stock
                          </Text>
                        )}
                      </div>
                    </GridCol>
                  </GridRow>

                  {idx < visibleItems.length - 1 && <Rule />}
                </Fragment>
              ))}
            </Grid>
          </div>
        )}

        <GridRow justify="normal" align="center">
          <GridCol xs={12}>
            <ButtonV2
              label={abandonedBasketButtonText}
              onClick={() => setBasketOverlayOpen(false)}
              buttonStyle="primary"
              size="m"
              isFullWidth
              href={linkToBasketPage}
            />
          </GridCol>
        </GridRow>
      </Grid>
    </div>
  );

  const TriggerContent = (
    <>
      <Icon icon={ICON_BASKET} label="Basket" size={24} />
      {items?.length > 0 && (
        <span
          aria-label={`${items.length} items in basket`}
          style={{
            position: 'absolute',
            top: -4,
            right: -6,
            minWidth: 16,
            height: 16,
            borderRadius: '50%',
            background: '#ebf304',
            color: '#000',
            fontSize: 10,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}
        >
          {items.length}
        </span>
      )}
    </>
  );

  const Trigger = renderTrigger ? (
    renderTrigger({
      onClick: toggleBasket,
      onKeyDown: handleTriggerKey,
      'aria-expanded': basketOverlayOpen,
      'aria-label': 'Open basket',
    })
  ) : (
    <button
      ref={basketTriggerRef}
      className="custom-header-icon"
      aria-label="Open basket"
      onClick={toggleBasket}
      onKeyDown={handleTriggerKey}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
      }}
      aria-expanded={basketOverlayOpen}
    >
      {TriggerContent}
    </button>
  );

  if (isSmallScreen) {
    return (
      <>
        {Trigger}
        {basketOverlayOpen && (
          <dialog
            open
            aria-labelledby="basket-title"
            className="eeb-BasketSheet"
            style={{
              position: 'fixed',
              top: sheetTop,
              left: 0,
              right: 0,
              width: '100vw',
              background: '#fff',
              zIndex: 90,
              boxShadow: '0 8px 24px rgba(0,0,0,.2)',
              borderRadius: '0 0 12px 12px',
              overflow: 'hidden',
              border: 'none',
              margin: 0,
            }}
          >
            {Content}
          </dialog>
        )}
      </>
    );
  }

  return (
    <Popover
      open={basketOverlayOpen}
      onOpenChange={setBasketOverlayOpen}
      side="bottom"
      align="end"
      arrow
      ariaLabel="Basket popover"
      content={
        <div style={{ minWidth: 320, maxHeight: '69vh' }}>{Content}</div>
      }
    >
      {Trigger}
    </Popover>
  );
};

export default BasketPopover;
